import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../utils/api';
import { isAuthenticated } from '../utils/auth';
import { config } from '../config';
import { BackgroundImage } from './BackgroundImage';
import AppLoader from './AppLoader';
import { Capacitor } from '@capacitor/core';
import { getPendingSyncStatus } from '../tracking/nativeTracker';
import { useT } from '../i18n/I18nProvider';
import '../styles/MyRoutes.css';
import '../styles/RoutesList.css';

export const MyRoutes = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Rute koje čekaju sinhronizaciju (neposlate offline tačke u lokalnoj SQLite bazi).
  const [pendingSync, setPendingSync] = useState({ routeIds: [], totalPoints: 0 });
  const userIsAuthenticated = isAuthenticated();

  useEffect(() => {
    if (!userIsAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchMyRoutes = async () => {
      try {
        console.log('About to fetch my routes...'); // Debug log
        console.log('API URL:', config.apiUrl); // Debug log
        console.log('Full URL will be:', `${config.apiUrl}/my_routes`); // Debug log
        
        // This endpoint should return only routes created by the current user
        const data = await authenticatedFetch('/my_routes');
        console.log('My routes response:', data); // Debug log
        console.log('Routes data:', data.data); // Debug log
        console.log('Routes count:', data.data ? data.data.length : 0); // Debug log
        setRoutes(data.data || []);
      } catch (error) {
        console.error('Error fetching my routes:', error); // Debug log
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMyRoutes();
  }, [userIsAuthenticated]);

  // Učitaj koje rute čekaju sinhronizaciju (offline tačke na disku). Samo native.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    getPendingSyncStatus().then((s) => { if (!cancelled) setPendingSync(s); });
    return () => { cancelled = true; };
  }, []);

  const handleEditRoute = (routeId) => {
    navigate(`/routes/${routeId}/edit`);
  };

  const handleDeleteRoute = async (routeId) => {
    setDeleting(routeId);
    setError(null);
    try {
      await authenticatedFetch(`/routes/${routeId}`, {
        method: 'DELETE'
      });
      // Ako nije bacio, brisanje je uspelo – ukloni rutu iz liste i zatvori modal
      setRoutes(prevRoutes => prevRoutes.filter(route => route.id !== routeId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting route:', err);
      setError(err.message || 'Greška pri brisanju rute');
    } finally {
      setDeleting(null);
    }
  };

  const confirmDelete = (route) => {
    setDeleteConfirm(route);
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  if (!userIsAuthenticated) {
    return (
      <div className="routes-page">
        <div className="routes-background">
          <BackgroundImage src="/img/routes-bgd.jpg" alt="" className="routes-bg-image" fetchPriority="low" />
          <div className="routes-overlay" />
        </div>
      <div className="page-container">
        <div className="page-header">
          <h1>Moje rute</h1>
        </div>
        <div className="alert-info-modern">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem' }}>🔐 Potrebna prijava</h3>
          <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem' }}>Morate biti ulogovani da biste videli svoje rute.</p>
          <Link to="/login" className="btn-primary-modern">Uloguj se</Link>
        </div>
      </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="routes-page">
        <div className="routes-background">
          <BackgroundImage src="/img/routes-bgd.jpg" alt="" className="routes-bg-image" fetchPriority="low" />
          <div className="routes-overlay" />
        </div>
        <div className="page-container">
          <AppLoader title="Učitavanje ruta..." subtitle="Prikupljamo vaše sačuvane rute." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="routes-page">
        <div className="routes-background">
          <BackgroundImage src="/img/routes-bgd.jpg" alt="" className="routes-bg-image" fetchPriority="low" />
          <div className="routes-overlay" />
        </div>
      <div className="page-container">
        <div className="page-header">
          <h1>Moje rute</h1>
        </div>
        <div className="alert-error-modern">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem' }}>⚠️ Greška</h3>
          <p style={{ margin: 0, fontSize: '1.1rem' }}>{error}</p>
        </div>
      </div>
      </div>
    );
  }

  // Rute koje čekaju sinhronizaciju (poređenje kao string jer native vraća string id-jeve).
  const pendingIdSet = new Set(pendingSync.routeIds);
  const hasPendingSync = pendingSync.routeIds.length > 0 || pendingSync.totalPoints > 0;

  // Filter routes based on search term
  const filteredRoutes = routes.filter(route =>
    route.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (route.description && route.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="routes-page">
      <div className="routes-background">
        <BackgroundImage src="/img/routes-bgd.jpg" alt="" className="routes-bg-image" fetchPriority="low" />
        <div className="routes-overlay" />
      </div>
    <div className="page-container">
      <div className="page-header clean">
        <h1>Moje rute</h1>
      </div>
      
      <div className="glass-card">
        {hasPendingSync && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            background: 'rgba(17,153,142,0.15)', border: '1px solid rgba(56,239,125,0.4)',
            borderRadius: '12px', padding: '0.85rem 1rem', marginBottom: '1.25rem',
            color: '#e8fdf2', fontSize: '0.9rem', lineHeight: 1.5,
          }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>⏳</span>
            <span>{t('sync.pendingBanner')}</span>
          </div>
        )}
        <div className="header-with-button">
          <Link to="/new-route" className="btn-primary-modern" style={{ borderRadius: '8px' }}>
            + Dodaj novu rutu
          </Link>
        </div>

        {/* Search Input */}
        {routes.length > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1.5rem' }}>
            <input
              type="text"
              placeholder="🔍 Pretraži svoje rute..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input-modern"
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                fontSize: '1rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                transition: 'all 0.3s ease'
              }}
            />
            {searchTerm && (
              <p style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                Pronađeno ruta: {filteredRoutes.length}
              </p>
            )}
          </div>
        )}

        {routes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🗺️</div>
            <h3 className="text-white-modern" style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>Nema ruta</h3>
            <p className="text-white-secondary" style={{ marginBottom: '2rem', fontSize: '1.1rem' }}>Još uvek niste kreirali nijednu rutu.</p>
            <Link to="/new-route" className="btn-primary-modern" style={{ borderRadius: '8px' }}>
              Kreiraj prvu rutu
            </Link>
          </div>
        ) : filteredRoutes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem 2rem',
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>🔍</p>
            <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>
              Nema ruta koje odgovaraju pretrazi
            </p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Pokušaj sa drugim terminom za pretragu
            </p>
          </div>
        ) : (
          <div className="routes-grid">
            {filteredRoutes.map((route, index) => (
              <div key={`${route.id}-${index}`} className="route-card">
                <div className="route-card-header">
                  <h3 className="route-title">{route.title}</h3>
                  <span className="route-difficulty">{route.difficulty}</span>
                </div>
                <div className="route-card-content">
                  {pendingIdSet.has(String(route.id)) && (
                    <span style={{
                      display: 'inline-block', marginBottom: '0.6rem',
                      background: 'rgba(255,193,7,0.18)', border: '1px solid rgba(255,193,7,0.5)',
                      color: '#ffd54a', borderRadius: '999px', padding: '0.15rem 0.6rem',
                      fontSize: '0.72rem', fontWeight: 600,
                    }}>⏳ {t('sync.pendingBadge')}</span>
                  )}
                  <p className="route-description">{route.description}</p>
                  <div className="route-stats">
                    <span className="route-duration">
                      <i className="icon-time"></i>
                      {Math.floor(route.duration / 60)}h {route.duration % 60}m
                      {route.calculated_from_points && (
                        <small style={{ color: '#28a745', marginLeft: '5px' }}>📍</small>
                      )}
                    </span>
                    <span className="route-distance">
                      <i className="icon-distance"></i>
                      {route.distance}km
                      {route.calculated_from_points && (
                        <small style={{ color: '#28a745', marginLeft: '5px' }}>📍</small>
                      )}
                    </span>
                  </div>
                </div>
                <div className="route-card-footer" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Link to={`/route/${route.id}`} className="btn-primary-modern" style={{ 
                    flex: 1, 
                    minWidth: '120px',
                    borderRadius: '8px'
                  }}>
                    Pogledaj detalje
                  </Link>
                  <button 
                    className="btn-secondary-modern"
                    onClick={() => handleEditRoute(route.id)}
                    style={{ 
                      flex: 1, 
                      minWidth: '80px',
                      borderRadius: '8px',
                      background: 'rgba(17, 153, 142, 0.8)',
                      borderColor: 'rgba(17, 153, 142, 0.9)',
                      color: 'rgba(255, 255, 255, 0.95)'
                    }}
                  >
                    Uredi
                  </button>
                  <button 
                    className="btn-secondary-modern"
                    onClick={() => confirmDelete(route)}
                    disabled={deleting === route.id}
                    style={{ 
                      flex: 1, 
                      minWidth: '80px',
                      borderRadius: '8px',
                      background: deleting === route.id ? 'rgba(255, 107, 107, 0.4)' : 'rgba(255, 107, 107, 0.3)',
                      borderColor: 'rgba(255, 107, 107, 0.5)',
                      color: 'rgba(255, 255, 255, 1)',
                      fontWeight: '600',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    {deleting === route.id ? 'Brisanje...' : 'Obriši'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Delete Confirmation Modal – renderovan u body da bude uvek centriran na ekranu */}
        {deleteConfirm && createPortal(
          <div className="delete-modal-overlay" onClick={cancelDelete} role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
            <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
              <h3 id="delete-modal-title">Potvrda brisanja</h3>
              <p>Da li ste sigurni da želite da obrišete rutu <strong>"{deleteConfirm.title}"</strong>?</p>
              <p className="delete-warning">Ova akcija se ne može poništiti.</p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
                <button 
                  className="btn-secondary-modern"
                  onClick={cancelDelete}
                  style={{ borderRadius: '8px' }}
                >
                  Otkaži
                </button>
                <button 
                  className="btn-primary-modern"
                  onClick={() => handleDeleteRoute(deleteConfirm.id)}
                  disabled={deleting === deleteConfirm.id}
                  style={{ 
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
                    boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)'
                  }}
                >
                  {deleting === deleteConfirm.id ? 'Brisanje...' : 'Obriši'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
    </div>
  );
};

export default MyRoutes;
